import { getNodeApiBaseUrl } from "./nodeApiBaseUrl.ts";
import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";
import {
  buildBulkCameraPlacementsBody,
  mapSectionCamerasFromApi,
} from "../pages/administration/patrolAdmin/patrolSectionCameraLocation.js";

const DRAWINGS_PATH = "/drawings";
const SECTION_CAMERAS_PATH = "/patrol-section-cameras";
const SECTION_CAMERAS_BULK_PATH = "/patrol-section-cameras/bulk";

export type CreatePatrolDrawingPayload = {
  file: File;
  name: string;
  description?: string;
  isFavorite?: boolean;
};

export type UpdatePatrolDrawingPayload = {
  name: string;
  description?: string;
  isFavorite?: boolean;
  file?: File;
};

export type PatrolDrawingUi = {
  id: string;
  title: string;
  description: string;
  isFavorite: boolean;
  fileUrl: string | null;
  fileType: string;
  fileName: string;
  imagePreview: string | null;
  cameraPlacements: unknown[];
  markers: unknown[];
};

export function getPatrolApiBase(): string {
  return getNodeApiBaseUrl();
}

function drawingPath(id: string): string {
  return `${DRAWINGS_PATH}/${encodeURIComponent(id)}`;
}

function buildUrl(path: string): string {
  const base = getPatrolApiBase();
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!base) return p;
  return `${base}${p}`;
}

function resolveFileUrl(url: unknown): string | null {
  if (!url) return null;
  const s = String(url).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("blob:")) return s;
  const base = getPatrolApiBase();
  if (!base) return s.startsWith("/") ? s : `/${s}`;
  return `${base}${s.startsWith("/") ? s : `/${s}`}`;
}

function unwrapEntity(body: unknown): unknown {
  if (body == null) return null;
  if (Array.isArray(body)) return body;
  const o = body as Record<string, unknown>;
  if (Array.isArray(o.data)) return o.data;
  if (o.data && typeof o.data === "object") return o.data;
  if (o.drawing && typeof o.drawing === "object") return o.drawing;
  return body;
}

function unwrapList(body: unknown): Record<string, unknown>[] {
  if (Array.isArray(body)) return body as Record<string, unknown>[];
  const o = body as Record<string, unknown> | null;
  if (Array.isArray(o?.data)) return o.data as Record<string, unknown>[];
  if (Array.isArray(o?.drawings))
    return o.drawings as Record<string, unknown>[];
  if (Array.isArray(o?.items)) return o.items as Record<string, unknown>[];
  return [];
}

function parseError(data: unknown, status: number): string {
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const detail = o.detail;
    const message =
      (typeof o.message === "string" && o.message) ||
      (typeof detail === "string" && detail) ||
      (Array.isArray(detail)
        ? detail
            .map((d) => {
              const row = d as Record<string, unknown>;
              return row.msg || row.message;
            })
            .filter(Boolean)
            .join(" ")
        : null) ||
      (typeof o.error === "string" && o.error) ||
      `Request failed (${status})`;
    return message;
  }
  return `Request failed (${status})`;
}

async function parseJsonResponse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function authHeaders(json = false): Record<string, string> {
  const token = tokenManager.getToken();
  return {
    Accept: "application/json",
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...userTimezoneHeaders(),
  };
}

/** Map API drawing → Patrol settings UI model. */
export function mapDrawingFromApi(raw: unknown): PatrolDrawingUi | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = row.id ?? row.drawingId ?? row._id;
  const title = row.name ?? row.title ?? "";
  const fileUrl = resolveFileUrl(
    row.fileUrl ??
      row.storageUrl ??
      row.url ??
      row.storage_path ??
      row.storagePath,
  );
  const fileName = String(row.fileName ?? row.file_name ?? "");
  const fileType =
    (row.fileType as string) ??
    (row.file_type as string) ??
    (fileName.toLowerCase().endsWith(".pdf") ? "pdf" : "image");

  const cameraPlacements =
    row.cameraPlacements ?? row.camera_placements ?? row.placements ?? [];

  return {
    id: String(id),
    title: String(title),
    description: String(row.description ?? ""),
    isFavorite: Boolean(row.isFavorite ?? row.is_favorite),
    fileUrl,
    fileType,
    fileName,
    imagePreview: fileType === "image" ? fileUrl : null,
    cameraPlacements: Array.isArray(cameraPlacements) ? cameraPlacements : [],
    markers: [],
  };
}

/** POST /api/drawings (multipart). */
export async function createPatrolDrawing(
  payload: CreatePatrolDrawingPayload,
): Promise<PatrolDrawingUi> {
  const { file, name, description = "", isFavorite = false } = payload;

  const form = new FormData();
  form.append("file", file);
  form.append("name", name);
  form.append("description", description);
  form.append("isFavorite", String(Boolean(isFavorite)));

  const res = await fetch(buildUrl(DRAWINGS_PATH), {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });

  const data = await parseJsonResponse(res);
  if (!res.ok) {
    throw new Error(parseError(data, res.status));
  }

  const entity = unwrapEntity(data);
  const mapped = mapDrawingFromApi(entity);
  if (!mapped?.id) {
    throw new Error("Drawing created but response had no id.");
  }
  return mapped;
}

/** PUT /api/drawings/:id */
export async function updatePatrolDrawing(
  id: string,
  payload: UpdatePatrolDrawingPayload,
): Promise<PatrolDrawingUi> {
  const { name, description = "", isFavorite = false, file } = payload;

  let res: Response;
  if (file) {
    const form = new FormData();
    form.append("file", file);
    form.append("name", name);
    form.append("description", description);
    form.append("isFavorite", String(Boolean(isFavorite)));
    res = await fetch(buildUrl(drawingPath(id)), {
      method: "PUT",
      headers: authHeaders(),
      body: form,
    });
  } else {
    res = await fetch(buildUrl(drawingPath(id)), {
      method: "PUT",
      headers: authHeaders(true),
      body: JSON.stringify({
        name,
        description,
        isFavorite: Boolean(isFavorite),
      }),
    });
  }

  const data = await parseJsonResponse(res);
  if (!res.ok) {
    throw new Error(parseError(data, res.status));
  }

  const entity = unwrapEntity(data);
  const mapped = mapDrawingFromApi(entity);
  if (!mapped?.id) {
    throw new Error("Drawing updated but response had no id.");
  }
  return mapped;
}

/** DELETE /api/drawings/:id */
export async function deletePatrolDrawing(id: string): Promise<void> {
  const res = await fetch(buildUrl(drawingPath(id)), {
    method: "DELETE",
    headers: authHeaders(),
  });

  if (res.ok || res.status === 204) return;

  const data = await parseJsonResponse(res);
  throw new Error(parseError(data, res.status));
}

/** GET /api/drawings */
export async function fetchPatrolDrawings(): Promise<PatrolDrawingUi[]> {
  const res = await fetch(buildUrl(DRAWINGS_PATH), {
    method: "GET",
    headers: authHeaders(),
  });

  const data = await parseJsonResponse(res);
  if (!res.ok) {
    throw new Error(parseError(data, res.status));
  }

  return unwrapList(data)
    .map(mapDrawingFromApi)
    .filter((d): d is PatrolDrawingUi => d != null);
}

/** GET /api/patrol-section-cameras?drawingId={id} */
export async function fetchPatrolSectionCameras(
  drawingId: string,
): Promise<ReturnType<typeof mapSectionCamerasFromApi>> {
  const params = new URLSearchParams({ drawingId: String(drawingId) });
  const res = await fetch(`${buildUrl(SECTION_CAMERAS_PATH)}?${params}`, {
    method: "GET",
    headers: authHeaders(),
  });

  const data = await parseJsonResponse(res);
  if (!res.ok) {
    throw new Error(parseError(data, res.status));
  }

  return mapSectionCamerasFromApi(data);
}

/** POST /api/patrol-section-cameras/bulk */
export async function savePatrolSectionCamerasBulk(
  drawingId: string,
  placements: Parameters<typeof buildBulkCameraPlacementsBody>[1],
): Promise<unknown> {
  const body = buildBulkCameraPlacementsBody(drawingId, placements);

  const res = await fetch(buildUrl(SECTION_CAMERAS_BULK_PATH), {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });

  const data = await parseJsonResponse(res);
  if (!res.ok) {
    throw new Error(parseError(data, res.status));
  }

  return data;
}

/** DELETE /api/patrol-section-cameras/:id */
export async function deletePatrolSectionCamera(
  placementId: string,
): Promise<void> {
  const res = await fetch(
    `${buildUrl(SECTION_CAMERAS_PATH)}/${encodeURIComponent(placementId)}`,
    {
      method: "DELETE",
      headers: authHeaders(),
    },
  );

  const data = await parseJsonResponse(res);
  if (!res.ok) {
    throw new Error(parseError(data, res.status));
  }
}
